import { HttpErrorResponse } from '@angular/common/http';

type AuthDiagnosticLevel = 'info' | 'warn' | 'error';

const SENSITIVE_KEY_PATTERN = /(token|secret|password|authorization|cookie|code)/i;
const MAX_STRING_LENGTH = 180;

let traceSequence = 0;

export function createAuthTraceId(prefix = 'crm-auth'): string {
  traceSequence = (traceSequence + 1) % Number.MAX_SAFE_INTEGER;

  return [
    prefix,
    Date.now().toString(36),
    traceSequence.toString(36),
    Math.random().toString(36).slice(2, 8),
  ].join('-');
}

export function authLog(
  level: AuthDiagnosticLevel,
  event: string,
  details: Record<string, unknown> = {}
): void {
  console[level](`[auth-diagnostics] ${event}`, redact(details));
}

export function describeHttpError(error: unknown): Record<string, unknown> {
  if (error instanceof HttpErrorResponse) {
    return {
      name: error.name,
      status: error.status,
      statusText: error.statusText,
      url: sanitizeUrl(error.url),
      message: truncate(error.message),
      backendMessage: extractBackendMessage(error.error),
    };
  }

  return describeError(error);
}

export function describeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: truncate(error.message),
    };
  }

  return {
    message: truncate(String(error)),
  };
}

export function sanitizeUrl(url?: string | null): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname;
  } catch (error) {
    return url.split(/[?#]/)[0] || null;
  }
}

function extractBackendMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    return truncate(value);
  }

  if (Array.isArray(value)) {
    return truncate(value.map(extractBackendMessage).filter(Boolean).join(', '));
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return extractBackendMessage(record['message']) ?? extractBackendMessage(record['error']);
  }

  return null;
}

function redact(value: unknown, depth = 0): unknown {
  if (depth > 4) {
    return '[truncated]';
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => redact(item, depth + 1));
  }

  if (!value || typeof value !== 'object') {
    return typeof value === 'string' ? truncate(value) : value;
  }

  return Object.entries(value as Record<string, unknown>).reduce(
    (acc, [key, item]) => {
      acc[key] = shouldRedactKey(key)
        ? '[redacted]'
        : redact(item, depth + 1);
      return acc;
    },
    {} as Record<string, unknown>
  );
}

function shouldRedactKey(key: string): boolean {
  if (/fingerprint|expires|expiry|ttl|length|has[A-Z].*token|codeAlreadyActive/i.test(key)) {
    return false;
  }

  return SENSITIVE_KEY_PATTERN.test(key);
}

function truncate(value?: string | null): string | null {
  if (!value) {
    return value ?? null;
  }

  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...`
    : value;
}
